import { Rndm } from 'twrk/Rndm.js';
import { Arry } from 'twrk/Arry.js';
import { M2 } from 'twrk/M2.js';
import { XY } from 'twrk/V2.js';
import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { Wbgl_mono } from 'twrk/Wbgl_mono.js';
import { wbgl_type } from 'twrk/Wbgl.js';
import { Rapr2, rapr2_body_t } from 'twrk/Rapr2.js';

import { twrk_type } from './types.js';
import { rope_make, anchor_world } from './rope.js';
import { sprite_load, sprite_make, sprite_t } from './sprite.js';
import { zoom, pan } from './camera.js';
import { asset } from './asset.js';

//the catcher is a goose tied to the rope by its front leg, hanging upside down and swinging
//perpetually like a pendulum, ready to catch the swinger's hands with its beak once released.
//It's an NPC: no keyboard input, never lets go. Bone chain from the rope down: front leg,
//torso, neck, head — the wing and the back leg just hang off the torso and flap along.

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "catcher_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            uniform vec2 u_screen;
            uniform float u_zoom;
            uniform vec2 u_pan;
            void main() {
                gl_Position = vec4((position - u_pan) * u_zoom * u_screen, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform vec4 u_color;
            out vec4 fragColor;
            void main() {
                fragColor = u_color;
            }
            `
        });
    }
    return _shader;
}

//the goose parts are 960x960 layers sharing one frame: drawn upright (feet down, beak up),
//the physics flips them head-under once the whole bird hangs by the front leg
const FRAME = 960;
const SRC = {
    torso: asset('goose/torso.png'),
    neck: asset('goose/neck.png'),
    head: asset('goose/head.png'),
    leg_front: asset('goose/leg_front.png'),
    leg_back: asset('goose/leg_back.png'),
    wing_front: asset('goose/wing_front.png'),
};

//joint pivots inside the shared frame, image px (y down). Bone lengths and sprite pins both
//derive from these, so the parts recompose the drawn goose exactly when the chain is at rest.
const IMG = {
    foot: { x: 508, y: 855 }, //front ankle, where the rope ties on
    hip: { x: 488, y: 700 },
    shoulder: { x: 484, y: 415 }, //torso-neck joint, also the wing root
    head_joint: { x: 479, y: 300 },
    beak: { x: 487, y: 45 }, //the catch point
    hip_back: { x: 436, y: 690 },
    foot_back: { x: 430, y: 880 },
    wing_root: { x: 490, y: 415 },
    wing_tip: { x: 330, y: 500 },
};

export async function catcher_load(Canvas: wbgl_type) {
    await sprite_load(Canvas, Object.values(SRC));
}

type catcher_input = {
    id?: string;
    x?: number;
    y?: number; //front-foot/rope-grip rest position
    rope?: number;
};

const presets = {
    rope: 2,
    size: 1.0, //world height of the full 960px goose frame
    mass_leg: 0.1,
    mass_torso: 0.3,
    mass_neck: 0.1,
    mass_head: 0.12,
    mass_wing: 0.04,
    mass_leg_back: 0.08,
    swing: 1.5, //initial sideways velocity, world units/s
    joint_stiff: 2, //motor stiffness on the chain joints, at all times
    wing_stiff: 0.5, //barely-there motor pulling the wing back to its drawn pose, so it flaps freely
    wing_range: 1.3, //how far the wing may fold away from that pose, either way (twrk rad)
    leg_back_stiff: 1.5, //loose motor keeping the back leg alongside the torso, wobbling behind
    leg_back_range: 1.5,
    perpetual_kick: 1, //velocity re-injected at each turnaround so the swing never decays
    catch_tolerance: 0.5, //max world-unit gap between beak and hand for the catch to count; raise to make catching easier
    catch_stiff: 400, //zero-rest-length spring holding the caught hand, same feel as the rope grip
    catch_damp: 20,
    release_spin: 6, //angular velocity kick (rad/s) given to all bodies when letting go of the rope, so it tumbles nicely
};

const dist = (a: XY, b: XY) => Math.hypot(b.x - a.x, b.y - a.y);

export function catcher_manager(twrk: twrk_type) {

    const { Canvas, Scrn, Rapr, Dbug } = twrk;
    const shader = get_shader(Canvas);

    const list: any[] = [];

    function make(io: catcher_input = {}) {

        const id = io.id || Rndm.id();
        const rope_len = io.rope ?? presets.rope;
        const x = io.x ?? 0;
        const y_foot = io.y ?? -0.75; //rope's grip point (the front ankle), analogous to swinger's y_hand
        const k = presets.size / FRAME; //world units per frame px

        //bone half-lengths (anchor to anchor), straight from the frame's joint pivots
        const h_lg = dist(IMG.foot, IMG.hip) * k * 0.5;
        const h_to = dist(IMG.hip, IMG.shoulder) * k * 0.5;
        const h_ne = dist(IMG.shoulder, IMG.head_joint) * k * 0.5;
        const h_hd = dist(IMG.head_joint, IMG.beak) * k * 0.5;
        const h_wg = dist(IMG.wing_root, IMG.wing_tip) * k * 0.5;
        const h_lb = dist(IMG.hip_back, IMG.foot_back) * k * 0.5;

        //parts collide with nothing (not even each other), they only hang and swing
        const groups = Rapr2.groups(0b10, 0);

        const make_bone = (pos: XY, angle: number, half: number, radius: number, mass: number) =>
            Rapr.capsule({ pos, angle, half_height: half, radius, mass, groups, velocity_damp: 0.01, angular_damp: 0.01 });

        //hangs by the front leg: the rope ties to the ankle, the leg runs down to the hip
        //(body spawned flipped, angle π), and torso/neck dangle head-under with the beak at the
        //lowest point, free to catch. No knee fold needed — the rope grips the ankle directly.
        let yy = y_foot;
        const leg = make_bone({ x, y: yy + h_lg }, Math.PI, h_lg, 0.025, presets.mass_leg); yy += h_lg * 2;
        const torso = make_bone({ x, y: yy + h_to }, Math.PI, h_to, 0.05, presets.mass_torso); yy += h_to * 2;
        const y_shoulder = yy;
        const neck = make_bone({ x, y: yy + h_ne }, Math.PI, h_ne, 0.03, presets.mass_neck); yy += h_ne * 2;
        const head = make_bone({ x, y: yy + h_hd }, Math.PI, h_hd, 0.035, presets.mass_head);

        //back leg: hangs off the hip alongside the torso, foot up like the front one
        const y_hip = y_foot + h_lg * 2;
        const leg_back = make_bone({ x, y: y_hip - h_lb }, Math.PI, h_lb, 0.025, presets.mass_leg_back);

        //wing: rooted at the shoulder, spawned in its drawn pose (flipped with the rest of the
        //bird); twrk body angle from the desired axis (sin a, -cos a) = root→tip direction
        const wing_angle = Math.atan2(-(IMG.wing_tip.x - IMG.wing_root.x), IMG.wing_tip.y - IMG.wing_root.y);
        const wax = Math.sin(wing_angle), way = -Math.cos(wing_angle);
        const wing = make_bone({ x: x + wax * h_wg, y: y_shoulder + way * h_wg }, wing_angle, h_wg, 0.02, presets.mass_wing);

        //rope: hook to ankle, keeps swinging under its own inertia once let go of
        const rope = rope_make(twrk, {
            x, y_hook: y_foot - rope_len, len: rope_len, groups,
            grip_body: leg, grip_anchor: { x: 0, y: h_lg },
        });

        //hinges between the parts, weak motors give a bit of stiffness
        const jnt = presets.joint_stiff;
        const j_hip = Rapr.revolute({ a: torso, b: leg, anchor_a: { x: 0, y: h_to }, anchor_b: { x: 0, y: -h_lg }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_sho = Rapr.revolute({ a: neck, b: torso, anchor_a: { x: 0, y: h_ne }, anchor_b: { x: 0, y: -h_to }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_hed = Rapr.revolute({ a: head, b: neck, anchor_a: { x: 0, y: h_hd }, anchor_b: { x: 0, y: -h_ne }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_lgb = Rapr.revolute({ a: torso, b: leg_back, anchor_a: { x: 0, y: h_to }, anchor_b: { x: 0, y: -h_lb }, limits: [-presets.leg_back_range, presets.leg_back_range], motor: { stiffness: presets.leg_back_stiff } });
        //the wing shares the shoulder pivot and flaps freely around its drawn rest angle
        const wing_rest = wing_angle - Math.PI;
        const j_wng = Rapr.revolute({ a: torso, b: wing, anchor_a: { x: 0, y: -h_to }, anchor_b: { x: 0, y: h_wg }, limits: [wing_rest - presets.wing_range, wing_rest + presets.wing_range], motor: { stiffness: presets.wing_stiff } });
        j_wng.set_motor(wing_rest, presets.wing_stiff);

        const bodies = [leg, torso, neck, head, leg_back, wing];

        //initial push so the pendulum starts swinging
        for (const b of bodies) b.set_vel({ x: presets.swing, y: 0 });

        //perpetual swing: torso crossing the hook's x (center of the arc) is the low point of the
        //pendulum; give it a small same-direction kick there so the amplitude never decays away.
        //Stops once the rope is let go of — a free-flying chain link pumps nothing.
        let perpetual_last_x = 0;

        //letting go of the rope (not of the caught hand — the hold joint lives forever): once this
        //catcher has caught the chain's free end, releasing detaches its own foot from the rope so
        //catcher and chain fly on as one, the foot becoming the chain's new free end
        let released = false;
        const kick = () => {
            const dir = Math.sign(torso.vel.x) || 1;
            for (const b of bodies) b.set_ang_vel(b.ang_vel + dir * presets.release_spin);
        };
        const release = () => {
            if (released) return;
            released = true;
            rope.release();
            kick();
        };

        //snapshot of this catcher's own body chain + rope, taken right after it lets go of its own
        //rope, so a botched drop further down the chain can replay from right after that release
        //instead of from scratch. Only the station that was the *last* one released is meant to be
        //re-gripped on redo — main.ts decides that per call, same as the swinger's own rope.
        const snap_bodies = [...bodies, rope.hook, ...rope.segs];
        type snapshot_t = { pos: XY; angle: number; vel: XY; ang_vel: number };
        let release_snapshot: snapshot_t[] | null = null;
        const take_snapshot = () => {
            release_snapshot = snap_bodies.map(b => ({ pos: { x: b.pos.x, y: b.pos.y }, angle: b.angle, vel: { x: b.vel.x, y: b.vel.y }, ang_vel: b.ang_vel }));
        };
        const redo = (re_grip: boolean) => {
            if (!release_snapshot) return;
            for (let i = 0; i < snap_bodies.length; i++) {
                const b = snap_bodies[i], s = release_snapshot[i];
                b.set_pos(s.pos);
                b.set_angle(s.angle);
                b.set_vel(s.vel);
                b.set_ang_vel(s.ang_vel);
            }
            if (re_grip) { rope.re_grip(); released = false; }
            for (const b of bodies) b.sync_read();
            rope.sync_read();
        };

        //the catch: hold() ties a foreign body (the swinger's hand) to the beak with the same kind
        //of zero-rest-length spring the rope grip uses, so it holds the points together without
        //fighting either figure's joint motors. unhold() lets go again for the next flight.
        let hold_joint: any = null;
        const beak_anchor = { x: 0, y: -h_hd };
        const hold = (body: rapr2_body_t, anchor: XY) => {
            if (hold_joint) return;
            hold_joint = Rapr.spring({
                a: head, b: body, anchor_a: beak_anchor, anchor_b: anchor,
                rest_length: 0, stiffness: presets.catch_stiff, damping: presets.catch_damp,
            });
        };
        const unhold = () => {
            if (!hold_joint) return;
            hold_joint.kill();
            hold_joint = null;
        };

        //graphics: each part is a frame-cut sprite pinned to its bone's two joint anchors,
        //listed back to front — the torso fluff covers the neck base, the wing rides on top
        const parts: { gfx: sprite_t; ends: () => [XY, XY] }[] = [];
        const make_part = (src: string, p1: XY, p2: XY, ends: () => [XY, XY]) =>
            parts.push({ gfx: sprite_make(Canvas, Scrn, { src, frame: FRAME, size: presets.size, p1, p2 }), ends });

        make_part(SRC.leg_back, IMG.hip_back, IMG.foot_back, () => [anchor_world(leg_back, { x: 0, y: -h_lb }), anchor_world(leg_back, { x: 0, y: h_lb })]);
        make_part(SRC.neck, IMG.shoulder, IMG.head_joint, () => [anchor_world(neck, { x: 0, y: h_ne }), anchor_world(neck, { x: 0, y: -h_ne })]);
        make_part(SRC.torso, IMG.hip, IMG.shoulder, () => [anchor_world(torso, { x: 0, y: h_to }), anchor_world(torso, { x: 0, y: -h_to })]);
        make_part(SRC.leg_front, IMG.foot, IMG.hip, () => [anchor_world(leg, { x: 0, y: h_lg }), anchor_world(leg, { x: 0, y: -h_lg })]);
        make_part(SRC.head, IMG.head_joint, IMG.beak, () => [anchor_world(head, { x: 0, y: h_hd }), anchor_world(head, { x: 0, y: -h_hd })]);
        make_part(SRC.wing_front, IMG.wing_root, IMG.wing_tip, () => [anchor_world(wing, { x: 0, y: h_wg }), anchor_world(wing, { x: 0, y: -h_wg })]);

        //debug: a blue quad at every joint pivot, computed from the same twrk-local anchors the
        //physics joints use, so a quad in the wrong place means the joint really is wired wrong
        const joint_size = 0.03;
        const joint_ends: (() => XY)[] = [
            () => anchor_world(leg, { x: 0, y: h_lg }), //rope grip / front ankle
            () => anchor_world(leg, { x: 0, y: -h_lg }), //hip
            () => anchor_world(torso, { x: 0, y: -h_to }), //shoulder
            () => anchor_world(neck, { x: 0, y: -h_ne }), //head joint
            () => anchor_world(head, beak_anchor), //beak / catch point
            () => anchor_world(wing, { x: 0, y: h_wg }), //wing root
            () => anchor_world(leg_back, { x: 0, y: -h_lb }), //back hip
        ];
        const joint_gfx = joint_ends.map(ends => {
            const mesh = M2.shape.rect({ width: joint_size, height: joint_size });
            const gl = Wbgl_mono.make(Canvas, shader, Scrn, { mesh, uniforms: { u_color: [0.2, 0.4, 1, 1], u_zoom: 1, u_pan: [0, 0] } });
            return { ends, mesh, gl };
        });

        //the front foot, same twrk-local anchor the rope grips: once this catcher releases its
        //rope, this is the chain's new free end for the next catcher's beak to grab
        const foot_anchor = { x: 0, y: h_lg };

        const f = {
            id,
            hook: rope.hook, leg, torso, neck, head,
            hand: () => anchor_world(head, beak_anchor), //the beak: what catches the swinger's hand
            foot: () => anchor_world(leg, foot_anchor),
            foot_anchor,
            hold,
            unhold,
            holding: () => !!hold_joint,
            release,
            is_released: () => released,
            take_snapshot,
            redo,
            tolerance: presets.catch_tolerance,
            run: function () {

                for (const b of bodies) b.sync_read();
                rope.sync_read();

                if (!released) {
                    const rel = torso.pos.x - rope.hook.pos.x;
                    if (Math.sign(rel) !== 0 && Math.sign(rel) !== Math.sign(perpetual_last_x)) {
                        const dir = Math.sign(torso.vel.x) || Math.sign(rel);
                        for (const b of bodies) b.set_vel({ x: b.vel.x + dir * presets.perpetual_kick, y: b.vel.y });
                    }
                    perpetual_last_x = rel;
                }

                for (const p of parts) {
                    const [a, b] = p.ends();
                    p.gfx.render(a, b, zoom.value, pan);
                }

                if (Dbug.on) for (const j of joint_gfx) {
                    const p = j.ends();
                    M2.shape.rect({ width: joint_size, height: joint_size, vertices: j.mesh });
                    for (let i = 0; i < j.mesh.length; i += 2) {
                        j.mesh[i] += p.x;
                        j.mesh[i + 1] += p.y;
                    }
                    j.gl.mesh_update(j.mesh);
                    j.gl.uniforms.u_zoom = zoom.value;
                    j.gl.uniforms.u_pan = [pan.x, pan.y];
                    j.gl.render();
                }

                //rope drawn last so its curve renders on top of the goose/joint debug graphics
                rope.render(zoom.value, pan);
            },
            kill: function () {
                for (const p of parts) p.gfx.kill();
                for (const j of joint_gfx) j.gl.kill();
                if (hold_joint) hold_joint.kill();
                rope.kill();
                for (const j of [j_hip, j_sho, j_hed, j_lgb, j_wng]) j.kill();
                for (const b of bodies) b.kill();
                Arry.remove.id(list, id);
            },
        };

        list.push(f);
        return f;
    }

    return {
        list,
        make,
        get: (id: string) => Arry.get.id(list, id),
        kill: (id: string) => { const s = Arry.get.id(list, id); if (s) s.kill(); },
        run: function () {
            for (const s of list) s.run();
        },
    };
}
